// src/Services/HintService.js
import { doc, getDoc, updateDoc, increment } from "firebase/firestore";
import { db } from "../firebase";
import useCoinStore from "../stores/coinStore";
import analytics from "./analyticsService";
import { groqChatCompletion } from "./groqClient";

class HintService {
  calculateHintCost(hintType, totalHintsUsedThisRound) {
    if (hintType === "memoryJogger") {
      return 10; // Flat 10 coins for memory jogger
    } else if (hintType === "directAnswer") {
      return 20 + totalHintsUsedThisRound * 20; // Keep direct hint pricing as is
    }
    return 0;
  }

  async checkHintAvailability(userId, roomId, currentRound, hintType) {
    try {
      // Pro users get unlimited free Memory Jogger hints
      const isPro = useCoinStore.getState().isPro;
      if (isPro && hintType === "memoryJogger") {
        return {
          hasFreeHints: true,
          freeHintsCount: Infinity,
          cost: 0,
          hasEnoughCoins: true,
          currentCoins: useCoinStore.getState().coinBalance,
          totalHintsUsed: 0,
          isProFree: true,
        };
      }

      const userRef = doc(db, "users", userId);
      const userDoc = await getDoc(userRef);

      let freeHints = 0;
      if (userDoc.exists()) {
        const userData = userDoc.data();
        freeHints = userData.hintInventory?.[hintType] || 0;
      }

      const roomRef = doc(db, "rooms", roomId);
      const roomDoc = await getDoc(roomRef);

      if (!roomDoc.exists()) {
        throw new Error("Room not found");
      }

      const roomData = roomDoc.data();
      const totalHintsUsed =
        roomData.rounds?.[currentRound]?.hintUsage?.[userId]
          ?.totalHintsUsedThisRound || 0;

      const cost = this.calculateHintCost(hintType, totalHintsUsed);

      // Get coins from Zustand store instead of Firebase
      const userCoins = useCoinStore.getState().coinBalance;

      console.log(
        `💡 Hint check - Type: ${hintType}, Cost: ${cost}, User coins: ${userCoins}, Free hints: ${freeHints}`
      );

      return {
        hasFreeHints: freeHints > 0,
        freeHintsCount: freeHints,
        cost,
        hasEnoughCoins: userCoins >= cost,
        currentCoins: userCoins,
        totalHintsUsed,
      };
    } catch (error) {
      console.error("Error checking hint availability:", error);
      throw error;
    }
  }

  async generateMemoryJoggerHint(category, letter) {
    try {
      const content = await groqChatCompletion({
        model: "qwen/qwen3.8-27b",
        reasoning_effort: "none",
        messages: [
          {
            role: "system",
            content: `You generate helpful memory-jogger hints for any category (animals, foods, movies, names, etc.). Give a SHORT, vivid clue (max 10 words) that nudges toward the answer without revealing it. For names, use rhymes, nicknames, or iconic roles; for other items, use shape, color, sound, or fun facts. Keep it playful, clear, and instantly useful.`,
          },
          {
            role: "user",
            content: `Give me a memory jogger hint for the category "${category}" that starts with the letter "${letter}". Just give the hint, no extra text.`,
          },
        ],
        temperature: 0.9,
        max_tokens: 50,
      });

      return content.trim();
    } catch (error) {
      console.error("Error generating memory jogger hint:", error);
      throw error;
    }
  }

  async generateDirectAnswerHint(category, letter) {
    try {
      const content = await groqChatCompletion({
        model: "qwen/qwen3.8-27b",
        reasoning_effort: "none",
        messages: [
          {
            role: "system",
            content: `You provide ONE correct answer for word game categories. Give a common, well-known word that fits the category and starts with the specified letter. Return ONLY the word, nothing else.`,
          },
          {
            role: "user",
            content: `Give me ONE word for the category "${category}" that starts with the letter "${letter}". Return only the word.`,
          },
        ],
        temperature: 0.7,
        max_tokens: 20,
      });

      return content.trim();
    } catch (error) {
      console.error("Error generating direct answer hint:", error);
      throw error;
    }
  }

  async useHint(userId, roomId, currentRound, category, hintType) {
    try {
      const availability = await this.checkHintAvailability(
        userId,
        roomId,
        currentRound,
        hintType
      );

      if (!availability.hasFreeHints && !availability.hasEnoughCoins) {
        throw new Error("Not enough coins or free hints");
      }

      let hint;
      const letter = await this.getCurrentLetter(roomId);

      if (hintType === "memoryJogger") {
        hint = await this.generateMemoryJoggerHint(category, letter);
      } else if (hintType === "directAnswer") {
        hint = await this.generateDirectAnswerHint(category, letter);
      }

      const userRef = doc(db, "users", userId);
      const roomRef = doc(db, "rooms", roomId);

      if (availability.isProFree) {
        // Pro users: memory jogger is free, no inventory to deduct
      } else if (availability.hasFreeHints) {
        await updateDoc(userRef, {
          [`hintInventory.${hintType}`]: increment(-1),
        });
      } else {
        // Deduct coins from Zustand store
        const coinStore = useCoinStore.getState();
        const result = await coinStore.spendCoins(
          availability.cost,
          `hint_${hintType}`,
          roomId
        );

        if (!result.success) {
          throw new Error(result.message || "Failed to spend coins");
        }
      }

      const hintData = {
        used: true,
        hint: hint,
        costPaid: availability.hasFreeHints ? 0 : availability.cost,
        usedAt: new Date(),
        wasFree: availability.hasFreeHints,
      };

      if (hintType === "directAnswer") {
        hintData.answer = hint;
        hintData.penalty = true;
      }

      await updateDoc(roomRef, {
        [`rounds.${currentRound}.hintUsage.${userId}.totalHintsUsedThisRound`]:
          increment(1),
        [`rounds.${currentRound}.hintUsage.${userId}.categoryHints.${category}.${hintType}`]:
          hintData,
      });

      analytics.track("hint_used", {
        room_id: roomId,
        round_number: currentRound,
        hint_type: hintType,
        cost: hintData.costPaid,
      });

      return {
        success: true,
        hint,
        costPaid: availability.hasFreeHints ? 0 : availability.cost,
        wasFree: availability.hasFreeHints,
        remainingFreeHints: availability.hasFreeHints
          ? availability.freeHintsCount - 1
          : availability.freeHintsCount,
      };
    } catch (error) {
      console.error("Error using hint:", error);
      throw error;
    }
  }

  async getCurrentLetter(roomId) {
    const roomRef = doc(db, "rooms", roomId);
    const roomDoc = await getDoc(roomRef);

    if (!roomDoc.exists()) {
      throw new Error("Room not found");
    }

    return roomDoc.data().currentLetter || "";
  }

  async purchaseHintPack(userId, packType) {
    const packs = {
      starter: { memoryJogger: 3, directAnswer: 2, cost: 200 },
      pro: { memoryJogger: 5, directAnswer: 5, cost: 500 },
      emergency: { memoryJogger: 10, directAnswer: 0, cost: 400 },
    };

    const pack = packs[packType];
    if (!pack) {
      throw new Error("Invalid pack type");
    }

    try {
      // Check coins from Zustand
      const coinStore = useCoinStore.getState();
      const userCoins = coinStore.coinBalance;

      if (userCoins < pack.cost) {
        throw new Error("Not enough coins");
      }

      // Spend coins via Zustand
      const spendResult = await coinStore.spendCoins(
        pack.cost,
        `hint_pack_${packType}`
      );

      if (!spendResult.success) {
        throw new Error(spendResult.message || "Failed to purchase pack");
      }

      // Add hints to inventory
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, {
        "hintInventory.memoryJogger": increment(pack.memoryJogger),
        "hintInventory.directAnswer": increment(pack.directAnswer),
      });

      return {
        success: true,
        pack: packType,
        hints: pack,
        coinsSpent: pack.cost,
      };
    } catch (error) {
      console.error("Error purchasing hint pack:", error);
      throw error;
    }
  }
}

export const hintService = new HintService();
export default hintService;
